/**
 * Phase 182-R — Lightweight, anonymous pageview telemetry.
 * --------------------------------------------------------
 * Pings the backend ONCE per route mount with the page_path + an
 * anonymous session id stored in localStorage.  Fire-and-forget — never
 * blocks paint, never waits for a response, never raises.
 *
 * Privacy contract:
 *   • Session id is a v4 UUID stored locally; we never tie it to a user.
 *   • Backend additionally hashes user-agent + first IP octet as a soft
 *     fallback fingerprint — never the raw IP.
 *   • We do NOT call this on authenticated routes (the SaaS doesn't need
 *     it, and we want zero impact on the in-app experience).
 */
import { useEffect, useRef } from 'react';

const STORAGE_KEY = 'op_session_id';
const TELEMETRY_PATH = '/api/telemetry/pageview';

function ensureSessionId() {
  try {
    let id = window.localStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = (window.crypto?.randomUUID?.() || `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);
      window.localStorage.setItem(STORAGE_KEY, id);
    }
    return id;
  } catch {
    // Private mode / blocked storage — generate a one-shot id
    return `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * Record a single pageview.  Safe to call from a useEffect.
 *
 * @param {string} pagePath     The route (e.g. "/streamx/live")
 * @param {string} [productLine] Optional override — server autodetects from path
 */
export function recordPageView(pagePath, productLine) {
  try {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return;
    const url = `${base}${TELEMETRY_PATH}`;
    const body = JSON.stringify({
      page_path: pagePath || window.location.pathname,
      product_line: productLine || undefined,
      session_id: ensureSessionId(),
      referrer: document.referrer || undefined,
    });
    // Prefer sendBeacon — it survives navigation, doesn't block the
    // main thread, never throws on the caller.
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }
    // Fallback: keepalive fetch
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      mode: 'cors',
    }).catch(() => {});
  } catch {
    /* never throw out of telemetry */
  }
}

/**
 * React hook — sends one pageview when the component mounts (and again
 * when the path string changes inside the same SPA session).
 */
export function usePageViewTelemetry(pagePath, productLine) {
  const lastPath = useRef('');
  useEffect(() => {
    const path = pagePath || (typeof window !== 'undefined' ? window.location.pathname : '');
    if (!path || path === lastPath.current) return;
    lastPath.current = path;
    recordPageView(path, productLine);
  }, [pagePath, productLine]);
}
